/**
 * Utility for handling service response properties structure
 * Handles all cases: object types, array types, and regular properties
 */

/**
 * Get processed properties array from service response properties
 * @param {Object} properties - The service.response.properties object
 * @param {string} serviceCode - Optional service code for key prefix
 * @returns {Array} Array of processed property objects
 */
export const getProcessedProperties = (properties, serviceCode = "") => {
  if (!properties || typeof properties !== "object") {
    return [];
  }

  const result = [];

  Object.entries(properties).forEach(([key, prop]) => {
    // Handle array type with nested properties
    if (prop.type === "array" && prop.items?.properties) {
      const containerTitle = getContainerTitle(prop, key);

      // Process properties inside array items
      Object.entries(prop.items.properties).forEach(([subKey, subProp]) => {
        const fullKey = `${key}_${subKey}`;
        const prefixedKey = serviceCode ? `${fullKey}` : fullKey;

        result.push({
          key: fullKey,
          prefixedKey,
          isNested: true,
          parentKey: key,
          parentTitle: containerTitle,
          isArrayItem: true,
          property: subProp,
          title: getPropertyTitle(subProp, `${prop.title || key} ${subKey}`),
          type: subProp.type,
          editable: subProp?.editable,
          isCustom: subProp.isCustom,
          isNotary: subProp.isNotary,
          originalKey: subKey,
          arrayInfo: {
            isArray: true,
            itemsType: prop.items.type,
            arrayTitle: containerTitle,
          },
        });
      });
    }
    // Handle object type with properties
    else if (prop.type === "object" && prop.properties) {
      const containerTitle = getContainerTitle(prop, key);

      // Process nested properties
      Object.entries(prop.properties).forEach(([subKey, subProp]) => {
        const fullKey = `${key}_${subKey}`;
        const prefixedKey = serviceCode ? `${fullKey}` : fullKey;

        result.push({
          key: fullKey,
          prefixedKey,
          isNested: true,
          parentKey: key,
          parentTitle: containerTitle,
          isArrayItem: false,
          property: subProp,
          title: getPropertyTitle(subProp, `${prop.title || key} ${subKey}`),
          type: subProp.type,
          editable: subProp?.editable,
          isCustom: subProp.isCustom,
          isNotary: subProp.isNotary,
          originalKey: subKey,
        });
      });
    }
    // Handle regular properties
    else {
      const prefixedKey = serviceCode ? `${key}` : key;

      result.push({
        key,
        prefixedKey,
        isNested: false,
        isArrayItem: false,
        property: prop,
        title: getPropertyTitle(prop, key),
        type: prop.type,
        editable: prop?.editable,
        isCustom: prop.isCustom,
        isNotary: prop.isNotary,
        originalKey: key,
      });
    }
  });

  return result;
};

/**
 * Get container title for object or array properties
 * @param {Object} prop - The property object
 * @param {string} fallback - Fallback key if no title found
 * @returns {string} Container title
 */
export const getContainerTitle = (prop, fallback) => {
  return prop.title || prop.title_kz || prop.title_ru || prop.name || fallback;
};

/**
 * Get property title
 * @param {Object} prop - The property object
 * @param {string} fallback - Fallback key if no title found
 * @returns {string} Property title
 */
export const getPropertyTitle = (prop, fallback) => {
  return prop.title || prop.name || prop.title_kz || prop.title_ru || fallback;
};

/**
 * Get container groups for structured rendering (both objects and arrays)
 * @param {Object} properties - The service.response.properties object
 * @returns {Array} Array of container groups
 */
export const getContainerGroups = (properties) => {
  if (!properties || typeof properties !== "object") {
    return [];
  }

  const groups = [];

  Object.entries(properties).forEach(([key, prop]) => {
    // Include both object and array types as container groups
    if (
      (prop.type === "object" && prop.properties) ||
      (prop.type === "array" && prop.items?.properties)
    ) {
      groups.push({
        key,
        title: getContainerTitle(prop, key),
        properties: prop.properties || prop.items.properties,
        type: prop.type,
        isArray: prop.type === "array",
      });
    }
  });

  return groups;
};

/**
 * Get flat properties (non-container properties only)
 * @param {Object} properties - The service.response.properties object
 * @returns {Array} Array of non-container properties
 */
export const getFlatProperties = (properties) => {
  if (!properties || typeof properties !== "object") {
    return [];
  }

  const flatProps = [];

  Object.entries(properties).forEach(([key, prop]) => {
    // Only include properties that are not objects or arrays with nested properties
    if (prop.type !== "object" && prop.type !== "array") {
      flatProps.push({
        key,
        property: prop,
        title: getPropertyTitle(prop, key),
        type: prop.type,
        editable: prop?.editable,
        isCustom: prop.isCustom,
      });
    }
    // Also include arrays without nested properties
    else if (prop.type === "array" && !prop.items?.properties) {
      flatProps.push({
        key,
        property: prop,
        title: getPropertyTitle(prop, key),
        type: prop.type,
        editable: prop?.editable,
        isCustom: prop.isCustom,
        isArray: true,
      });
    }
  });

  return flatProps;
};

/**
 * Get all properties in a flat structure including nested ones
 * @param {Object} properties - The service.response.properties object
 * @param {string} serviceCode - Optional service code for key prefix
 * @returns {Array} Array of all properties in flat structure
 */
export const getAllPropertiesFlat = (properties, serviceCode = "") => {
  return getProcessedProperties(properties, serviceCode);
};

/**
 * Check if property is nested (either in object or array)
 * @param {Object} processedProp - The processed property object
 * @returns {boolean} True if property is nested
 */
export const isNestedProperty = (processedProp) => {
  return processedProp.isNested === true;
};

/**
 * Check if property is from an array item
 * @param {Object} processedProp - The processed property object
 * @returns {boolean} True if property is from array item
 */
export const isArrayItemProperty = (processedProp) => {
  return processedProp.isArrayItem === true;
};

/**
 * Get property by key (supports nested keys like "parent_child")
 * @param {Object} properties - The service.response.properties object
 * @param {string} key - The key to find (can be "parent_child" for nested)
 * @returns {Object|null} Found property or null
 */
export const getPropertyByKey = (properties, key) => {
  if (!properties || !key) return null;

  // Check if it's a nested key
  if (key.includes("_")) {
    const parts = key.split("_");
    const parentKey = parts[0];
    const childKey = parts.slice(1).join("_");

    const parentProp = properties[parentKey];

    // Check if parent is an object with properties
    if (parentProp?.type === "object" && parentProp.properties) {
      return parentProp.properties[childKey] || null;
    }

    // Check if parent is an array with items containing properties
    if (parentProp?.type === "array" && parentProp.items?.properties) {
      return parentProp.items.properties[childKey] || null;
    }
  }

  return properties[key] || null;
};

/**
 * Format key with service code prefix
 * @param {string} key - The original key
 * @param {string} serviceCode - Service code
 * @returns {string} Formatted key
 */
export const formatKeyWithServiceCode = (key, serviceCode) => {
  return serviceCode ? `${serviceCode}_${key}` : key;
};

/**
 * Get array properties (properties with type: "array")
 * @param {Object} properties - The service.response.properties object
 * @returns {Array} Array properties
 */
export const getArrayProperties = (properties) => {
  if (!properties || typeof properties !== "object") {
    return [];
  }

  const arrayProps = [];

  Object.entries(properties).forEach(([key, prop]) => {
    if (prop.type === "array") {
      arrayProps.push({
        key,
        property: prop,
        title: getContainerTitle(prop, key),
        hasNestedProperties: !!prop.items?.properties,
        nestedProperties: prop.items?.properties || null,
      });
    }
  });

  return arrayProps;
};

/**
 * Check if a property is a container (object or array with nested properties)
 * @param {Object} prop - The property object
 * @returns {boolean} True if property is a container
 */
export const isContainerProperty = (prop) => {
  if (!prop) return false;

  return (
    (prop.type === "object" && prop.properties) ||
    (prop.type === "array" && prop.items?.properties)
  );
};
